/* eslint-disable no-unused-vars */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as Styles from './FastCheckout.styles';
import { AlertV2 } from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import { useHistory, useParams } from 'react-router';
import {
  DDIS_BY_COUNTRY,
  DISCOUNT_TYPES,
  FAST_CHECKOUT_CREDIT_CARD_METHODS,
  FAST_CHECKOUT_INSTALLMENTS,
  FAST_CHECKOUT_PAYMENT_METHODS,
} from './FastCheckout.enum';
import { useResize } from '@/hooks/useResize';
import { useCreditCardPayment } from '@/hooks/billing/subscriptions/useCreditCardPayment';
import { CALLER } from '@/hooks/billing/subscriptions/usePaymentModal/usePaymentModal.types';
import { useDispatch, useSelector } from 'react-redux';
import LoaderPage from '../Loader/LoaderPage';
import { FastCheckoutSuccess, FastCheckoutCart, FastCheckoutHeader } from './components';
import { loadCreditCard } from '@/redux/actions/creditCard';
import { fastCheckoutActions } from '@/redux/modules';
import { isBRServer } from '@/utils/Validators/validation';
import { createValidator } from './utils/formValidators';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { config, COUNTRY } from '@/config';
import {
  loadCitiesByCountryAndState, loadCountries, loadStatesByCountry, loadZipCode, updateSummary,
} from '@/redux/actions/summary';
import { postalCodeMask } from '@/utils/Masks/inputMasks';
import { LATAM_COUNTRIES } from '@/config/application/operatingCountriesConfig';
import { generateRecaptchaToken, loadReCaptcha } from '@/utils/Application/reCaptcha';
import ReCaptcha from '@/components/ReCaptcha';
import { RECEIVE_UPDATE_SUMMARY } from '@/redux/actions/actionsTypes';
import { useFastCheckoutLoading, useFastCheckoutNavigation } from '@/hooks/fastCheckout';

loadReCaptcha();
const FastCheckout = () => {
  const { fastCheckout: locale, routes: routesLocale, myData: myDataLocale } = useLocale();
  const dispatch = useDispatch();
  const { pid } = useParams() || {};
  const history = useHistory();
  const { width } = useResize() || {};

  const { location } = history || {};
  const { state: routeOriginInfo } = location || {};
  const checkoutDataInfo = routeOriginInfo && routeOriginInfo.checkoutData;
  const {
    coupon, billingCycle, couponAvailable, serviceId, domain,
  } = checkoutDataInfo || {};

  const isMobile = width < 600;

  const summaryState = useSelector(state => state.summary);
  const phoneNumberCountry = useSelector(state => state.summary.phoneNumberCountry);
  const countriesCode = useSelector(state => state.countryPhoneCodes.countryCode);
  const currentCreditCard = useSelector(state => state.creditCard);
  const { product: productData, purchase } = useSelector(state => state.fastCheckout);

  const productContent = useMemo(() => (locale.products[pid] ? locale.products[pid] : {}), [locale.products, pid]);
  const countryCode = useMemo(() => {
    if (phoneNumberCountry && countriesCode && countriesCode[phoneNumberCountry]) {
      return `+${countriesCode[phoneNumberCountry].code}`;
    }
    return DDIS_BY_COUNTRY[COUNTRY] || DDIS_BY_COUNTRY.default;
  }, [phoneNumberCountry, countriesCode]);
  const defaultFormData = useMemo(() => ({
    address: { value: '', error: '' },
    numberAndComplement: { value: '', error: '' },
    birthCountry: { value: { name: '', id: 0, abbreviation: '' }, error: '' },
    birthDate: { value: '', error: '' },
    city: { value: { id: 0, name: '' }, error: '' },
    companyName: { value: '', error: '' },
    country: { value: { name: '', id: 0, abbreviation: '' }, error: '' },
    document: { value: '', error: '' },
    email: { value: '', error: '' },
    name: { value: '', error: '' },
    neighborhood: { value: '', error: '' },
    phone: { value: '', error: '' },
    postalCode: { value: '', error: '' },
    state: { value: { name: '', id: 0, code: '' }, error: '' },
    ddi: { value: countryCode, error: '' },
  }), [countryCode]);

  const productNotAdded = !locale.products[pid];
  // Note: If product has special rule for discount in addition to the whmcs (like studio) fastcheckout will need to receive a permission to show the discount in route params
  const canShowDiscount = (productContent.productHasSpecialRuleForDiscount && couponAvailable) || !productContent.productHasSpecialRuleForDiscount;
  const productDetails = useMemo(() => {
    if (productNotAdded) {
      return {
        productId: '',
        productName: '',
        productCycles: [],
        productDiscount: {},
        productDefaultCycle: '',
        productDefaultPaymentMethod: '',
        productVat: {},
        productIcon: '',
        productTotalValue: 0,
        productIsTrial: false,
        productFinalPrice: 0,
        productBasePrice: 0,
        productPriceWithVat: 0,
        productRenewalDate: '',
        productPaymentMethods: [],
        productInstallments: [],
      };
    }

    const productFinalPrice = () => {
      if (productContent.productHasSpecialRuleForDiscount) {
        if (couponAvailable) return productData.totalValue; // Note: If the product has a special rule for discount and its avaliable, use the "total value" that is product price + vat + discount
        if (productData.vat.length > 0) return productData.priceWithVat; // Note: If the product has a special rule for discount and its unavaliable and have vat, use the "price with vat" that is product price + vat
        return productData.price; // Note: If the product has a special rule for discount and its unavaliable and dont have vat, use the price that is product base price
      }
      return productData.totalValue; // Note: For products that dont have special rule for discount use the total value since backend will return the final value
    };

    const baseInfo = {
      productId: productData.id,
      productName: productContent.name,
      productCycles: productData.cycles,
      productDiscount: canShowDiscount ? productData.discount : {},
      productDefaultCycle: productContent.defaultCycle,
      productDefaultPaymentMethod: productContent.defaultPaymentMethod,
      productVat: productData.vat,
      productIcon: productContent.icon,
      productIsTrial: productContent.isTrial,
      productFinalPrice: productFinalPrice(),
      productBasePrice: productData.price,
      productPriceWithVat: productData.priceWithVat,
      productRenewalDate: productData.renewalDate,
      productPaymentMethods: productData.paymentMethods,
      productInstallments: productData.installments,
    };

    return { ...baseInfo };
  }, [productNotAdded, productData, canShowDiscount, productContent, couponAvailable]);
  const hasDefaultPaymentMethod = productDetails.productPaymentMethods.includes(productDetails.productDefaultPaymentMethod);
  // Note: The priority for default billing cycle: The one passed in route params, if didnt passed one, use the one passed in content
  const initialBillingCycle = billingCycle || productDetails.productDefaultCycle;
  // Note: The priority for default payment methods: The one passed in content if its exists on api array of payment methods, otherwise use the first one in array
  const initialPaymentMethod = hasDefaultPaymentMethod ? productDetails.productDefaultPaymentMethod : productDetails.productPaymentMethods[0];
  const byPassConfirmationPayment = productDetails.productIsTrial;
  const hasDiscount = productDetails.productDiscount.percentage || productDetails.productDiscount.value;

  const [userDataMapped, setUserDataMapped] = useState(false);
  const [disableUserDataFields, setDisableUserDataFields] = useState(false);
  const [isCcButtonEnabled, setCcButtonEnabled] = useState(true);
  const [isCcButtonLoading, setCcButtonLoading] = useState(false);
  const [creditCardMethod, setCreditCardMethod] = useState(FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW);
  const [error, setError] = useState({ error: false, message: '', title: '' });
  const [successPurchase, setSuccessPurchase] = useState(false);
  const [productCycle, setProductCycle] = useState(initialBillingCycle);
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  const [installments, setInstallments] = useState(FAST_CHECKOUT_INSTALLMENTS.BRASPAG_CREDIT_1X); // Note: Dont have a way to change installments yet
  const [countriesSelectOptions, setCountriesSelectOptions] = useState([]);
  const [isForeigner, setIsForeigner] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [userOriginalFormData, setUserOriginalFormData] = useState(defaultFormData);
  const [recaptchaToken, setRecaptchaToken] = useState(undefined);

  const brand = LATAM_COUNTRIES.includes(formData.country.value.abbreviation) ? formData.country.value.abbreviation : 'other';
  const isBRCountry = formData.country.value.abbreviation === 'BR';
  const {
    isLoading,
    loadingProgress,
    loadingUpdateSummary,
    loadingZipCode,
  } = useFastCheckoutLoading({ userDataMapped });
  const {
    handleBackNavigation,
    handleNavigateAfterSuccess,
  } = useFastCheckoutNavigation(routeOriginInfo, routesLocale);

  const handleRecaptchaToken = useCallback(token => setRecaptchaToken(token), []);

  const fetchCountries = useCallback(country => dispatch(loadCountries(country)), [dispatch]);

  const fetchStatesByCountry = useCallback(country => dispatch(loadStatesByCountry(country)), [dispatch]);

  const fetchPostalCode = useCallback(() => dispatch(loadZipCode(formData.postalCode.value)), [dispatch, formData]);

  const fetchCitiesByCountryAndState = useCallback((country, state) => dispatch(loadCitiesByCountryAndState(country, state)), [dispatch]);

  const validateFormField = useCallback((fieldName, fieldValue) => {
    const validator = createValidator({
      brand,
      isForeigner,
      summaryState,
      setIsCompany,
    });
    return validator(fieldName, fieldValue);
  }, [brand, isForeigner, summaryState]);

  const validateAndSetFormFieldError = useCallback((fieldName, fieldValue, preventLoadPostalCode, preventUpdateForm) => {
    const validationError = validateFormField(fieldName, fieldValue);

    if (preventUpdateForm) {
      return validationError;
    }

    const newFormData = formData;

    if (fieldName === 'postalCode' && !validationError && isBRCountry && !preventLoadPostalCode) {
      fetchPostalCode();
    }

    if (validationError) {
      newFormData[fieldName] = { ...newFormData[fieldName], error: myDataLocale.errors[typeof validationError === 'boolean' ? fieldName : validationError] };
    } else {
      newFormData[fieldName] = { ...newFormData[fieldName], error: '' };
    }

    setFormData(() => ({ ...newFormData }));
    return validationError;
  }, [
    validateFormField,
    formData,
    myDataLocale,
    fetchPostalCode,
    isBRCountry,
  ]);

  const hasFormValidationErrors = useCallback(({ preventUpdateForm } = {}) => {
    const fieldValidations = [
      { field: 'address', value: formData.address.value },
      { field: 'email', value: formData.email.value },
      { field: 'name', value: formData.name.value },
      { field: 'neighborhood', value: formData.neighborhood.value },
      { field: 'numberAndComplement', value: formData.numberAndComplement.value },
      { field: 'phone', value: formData.phone.value },
      { field: 'document', value: formData.document.value },
      { field: 'postalCode', value: formData.postalCode.value, preventLoadPostal: true },
      { field: 'companyName', value: formData.companyName.value, conditionToValidate: isCompany },
      { field: 'birthDate', value: formData.birthDate.value, conditionToValidate: !isCompany },
      { field: 'birthCountry', value: formData.birthCountry.value.name, conditionToValidate: isForeigner && isBRServer },
      { field: 'city', value: formData.city.value.name },
      { field: 'country', value: formData.country.value.name },
      { field: 'state', value: formData.state.value.name },
    ];

    return fieldValidations.reduce((hasError, validation) => {
      // NOTE: fields that has conditional, only is validate if conditional is true
      // eslint-disable-next-line no-prototype-builtins
      const byPassValidate = validation.hasOwnProperty('conditionToValidate') && !validation.conditionToValidate;
      if (byPassValidate) {
        return hasError;
      }

      const fieldHasError = validateAndSetFormFieldError(validation.field, validation.value, validation.preventLoadPostal || false, preventUpdateForm);
      return hasError || fieldHasError;
    }, false);
  }, [validateAndSetFormFieldError, formData, isCompany, isForeigner]);

  const handleFormChange = useCallback((fieldName, fieldValue) => {
    const newFormData = { ...formData };

    switch (fieldName) {
      case 'birthCountry':
      case 'country':
        newFormData[fieldName] = { ...newFormData[fieldName], value: summaryState.locationData.countries.find(contry => contry.id === fieldValue) };

        if (fieldName === 'country') {
          const brId = summaryState.locationData.countries.find(country => country.abbreviation === 'BR').id;
          const selectedBr = brId === fieldValue;
          if (selectedBr) {
            fetchStatesByCountry('br');
            fetchPostalCode();
          }
        }

        break;
      case 'numberAndComplement':
      case 'birthDate':
      case 'companyName':
      case 'document':
      case 'name':
      case 'phone':
      case 'neighborhood':
      case 'address':
        newFormData[fieldName] = { ...newFormData[fieldName], value: fieldValue };
        break;
      case 'state': {
        if (!isBRCountry) {
          const statesArray = (summaryState.locationData && summaryState.locationData.states) || [];
          const state = statesArray.find(singleState => singleState.code === fieldValue) || { name: fieldValue, id: 0, abbreviation: '' };
          newFormData[fieldName] = { ...newFormData[fieldName], value: { ...state } };
        }
        break;
      }
      case 'city': {
        if (!isBRCountry) {
          const citiesArray = (summaryState.locationData && summaryState.locationData.cities) || [];
          const city = citiesArray.find(singleCity => singleCity.name === fieldValue) || { id: 0, name: fieldValue };
          newFormData[fieldName] = { ...newFormData[fieldName], value: { ...city } };
        }
        break;
      }
      case 'postalCode':
        newFormData[fieldName] = { ...newFormData[fieldName], value: postalCodeMask[brand](fieldValue) };
        break;
      case 'knowledge':
      case 'mailSubscribe':
        newFormData[fieldName] = fieldValue;
        break;
      case 'ddi':
        newFormData[fieldName] = { ...newFormData[fieldName], value: fieldValue };
        break;
      case 'foreigner':
        setIsForeigner(fieldValue);
        if (fieldValue) {
          setIsCompany(false);
        }
        break;
      case 'isCompany':
        setIsCompany(fieldValue);
        break;
      default:
    }
    if (newFormData[fieldName] && newFormData[fieldName].error) {
      const hasError = validateFormField(fieldName, fieldValue);
      if (!hasError) {
        newFormData[fieldName] = { ...newFormData[fieldName], error: '' };
      }
    }
    setFormData(newFormData);
  }, [
    brand,
    validateFormField,
    isBRCountry,
    formData,
    fetchPostalCode,
    fetchStatesByCountry,
    summaryState,
  ]);

  const updateUserDetails = useCallback(async (onSuccessCallback) => {
    await generateRecaptchaToken(config.RECAPTCHA_SITE_KEY, 'fastcheckout').then((token) => {
      handleRecaptchaToken(token);
    });

    if (!loadingUpdateSummary) {
      const newUserData = {
        address1: formData.address.value,
        address2: formData.neighborhood.value,
        address3: formData.numberAndComplement.value,
        birthDate: (formData.birthDate && formData.birthDate.value),
        city: formData.city.value.name,
        companyName: isCompany ? formData.companyName.value : '',
        country: formData.country.value.abbreviation,
        document: formData.document.value,
        email: summaryState.email,
        emailMarketingAgreement: summaryState.mailSubscribe,
        id: summaryState.id,
        name: formData.name.value,
        nationality: isForeigner ? formData.birthCountry.value.abbreviation : summaryState.nationality,
        persona: summaryState.knowledge ? summaryState.knowledge : 0,
        phoneNumber: formData.phone.value.replace(/\D/g, ''),
        phoneNumberCountry: summaryState.phoneNumberCountry,
        postCode: formData.postalCode.value,
        recaptcha: recaptchaToken,
        state: (formData.state.value && formData.state.value.code) || (formData.state.value && formData.state.value.name),
        stateName: (formData.state.value && formData.state.value.name) || '',
      };

      dispatch(updateSummary(newUserData)).then((res) => {
        if (res.type === RECEIVE_UPDATE_SUMMARY) {
          onSuccessCallback();
          return;
        }

        if (res.data && (res.data.description === 'Duplicate Email Address')) {
          setError({ error: true, message: myDataLocale.snackBar.emailAlreadyExists });
          return;
        }

        setError({ error: true, message: myDataLocale.snackBar.updateError });
      });
    }
  }, [dispatch, formData, isCompany, isForeigner, myDataLocale, recaptchaToken, summaryState, setError, handleRecaptchaToken, loadingUpdateSummary]);

  const executePurchaseRequest = useCallback(() => {
    const discounts = [];
    if (hasDiscount) {
      const discountValue = productDetails.productDiscount.type === DISCOUNT_TYPES.PERCENTAGE ? productDetails.productDiscount.percentage.split('%').join('') : productDetails.productDiscount.value;

      discounts.push({
        value: parseFloat(discountValue),
        type: productDetails.productDiscount.type,
        name: coupon,
      });
    }

    dispatch(fastCheckoutActions.purchase.request({
      discount: discounts,
      product: [{
        serviceId: parseFloat(serviceId),
        productId: productDetails.productId,
        billingCycle: productCycle,
        domain: domain || '',
      }],
      paymentMethod: paymentMethod === FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD && isBRServer ? installments : paymentMethod,
    }));
  }, [productDetails, coupon, dispatch, installments, paymentMethod, productCycle, serviceId, hasDiscount, domain]);

  const { renderIframe, submitCreditCard, isIframeLoaded } = useCreditCardPayment({
    setCcButtonEnabled,
    setCcButtonLoading,
    caller: CALLER.FASTCHECKOUT,
    showIframeMiniCreditCard: false,
    setError,
    callFastCheckout: executePurchaseRequest,
  });

  const executePayment = useCallback(() => {
    switch (paymentMethod) {
      case FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD:
        if (creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW) {
          return submitCreditCard();
        }
        return executePurchaseRequest();
      default:
        return executePurchaseRequest();
    }
  }, [paymentMethod, creditCardMethod, submitCreditCard, executePurchaseRequest]);

  const handleFinalizeCheckoutProcess = useCallback(async (event) => {
    event.preventDefault();

    executePayment();
  }, [hasFormValidationErrors, disableUserDataFields, updateUserDetails, executePayment]);

  useEffect(() => {
    dispatch(loadCreditCard());
  }, [dispatch]);

  useEffect(() => {
    if (initialBillingCycle) {
      dispatch(fastCheckoutActions.product.request({
        pid,
        coupon,
        billingCycle: initialBillingCycle,
      }));
    }
  }, [dispatch, pid, routeOriginInfo, productDetails.productDefaultCycle, coupon, initialBillingCycle]);

  useEffect(() => {
    const isAUpgradeWithoutServiceId = productContent.isAUpgrade && !serviceId;
    if (productNotAdded || productData.error || isAUpgradeWithoutServiceId) {
      history.push(routesLocale.home);
      dispatch(fastCheckoutActions.reset());
    }
  }, [pid, locale.products, history, productNotAdded, routesLocale, productData.error, dispatch, productContent.isAUpgrade, serviceId]);

  useEffect(() => {
    if (purchase.success) {
      setSuccessPurchase(true);
    }
  }, [purchase.success]);

  useEffect(() => {
    if (purchase.error) {
      setError({
        error: true,
        title: locale.purchaseErrorToast.title,
        message: locale.purchaseErrorToast.description,
      });
    }
  }, [purchase.error, locale.purchaseErrorToast]);

  useEffect(() => {
    setPaymentMethod(initialPaymentMethod);
  }, [initialPaymentMethod]);

  useEffect(() => {
    setProductCycle(initialBillingCycle);
  }, [initialBillingCycle]);

  useEffect(() => {
    if (summaryState.locationData.countries && summaryState.locationData.countries.length) {
      const countriesOptionsArray = summaryState.locationData.countries.map(country => ({
        value: country.id,
        label: country.name,
      }));
      setCountriesSelectOptions(countriesOptionsArray);
    }
  }, [summaryState.locationData.countries]);

  useEffect(() => {
    setFormData({ ...defaultFormData, ddi: { value: countryCode, error: '' } });
  }, [countryCode, defaultFormData]);

  useEffect(() => {
    // load user address data and populate form
    fetchCountries(summaryState.country).then((countries) => {
      fetchStatesByCountry(summaryState.country).then((states) => {
        fetchCitiesByCountryAndState(summaryState.country, summaryState.state).then((cities) => {
          const countriesArray = (Array.isArray(countries.data) && countries.data) || summaryState.locationData.countries;
          const statesArray = (Array.isArray(states.data) && states.data) || summaryState.locationData.states;
          const citiesArray = (Array.isArray(cities.data) && cities.data) || summaryState.locationData.cities;
          const country = countriesArray && countriesArray.find(singleCountry => singleCountry.abbreviation === summaryState.country);
          const state = (statesArray && statesArray.find(singleState => singleState.code === summaryState.state)) || { name: summaryState.state, id: 0, abbreviation: '' };

          const city = (citiesArray && citiesArray.find(singleCity => singleCity.name === summaryState.city)) || { id: 0, name: summaryState.city };
          const birthCountry = countries.data && countries.data.find(singleCountry => singleCountry.abbreviation === summaryState.nationality);
          const isForeignerUser = summaryState.nationality !== 'BR' && isBRServer;

          if (isForeignerUser) {
            setIsForeigner(true);
            setIsCompany(false);
          }

          const formatCnpjCpf = value => (value.length === 11
            ? value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4')
            : value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '$1.$2.$3/$4-$5'));

          const document = isBRServer ? formatCnpjCpf(summaryState.document) : summaryState.document;

          if (((!isForeigner && isBRServer && document.length > 14) || (!isBRServer && summaryState.companyName))) {
            setIsCompany(true);
          }

          const isLatamBrand = country && LATAM_COUNTRIES.includes(country.abbreviation);

          const originalFormData = {
            ...formData,
            address: summaryState.address1 ? { value: summaryState.address1, error: '' } : formData.address,
            birthCountry: isForeignerUser && birthCountry ? { value: birthCountry, error: '' } : formData.birthCountry,
            birthDate: summaryState.birthDate ? { value: summaryState.birthDate, error: '' } : formData.birthDate,
            city: city && city.name ? { value: city, error: '' } : formData.city,
            country: country && country.name ? { value: country, error: '' } : formData.country,
            companyName: summaryState.companyName ? { value: summaryState.companyName, error: '' } : formData.companyName,
            document: document ? { value: document, error: '' } : formData.document,
            email: summaryState.email ? { value: summaryState.email, error: '' } : formData.email,
            knowledge: summaryState.persona,
            mailSubscribe: summaryState.emailMarketingAgreement,
            name: summaryState.firstname || summaryState.lastname ? { value: `${summaryState.firstname || ''} ${summaryState.lastname || ''}`, error: '' } : formData.name,
            neighborhood: summaryState.address2 ? { value: summaryState.address2, error: '' } : formData.neighborhood,
            numberAndComplement: summaryState.address3 ? { value: summaryState.address3, error: '' } : formData.numberAndComplement,
            phone: summaryState.phoneNumber ? { value: summaryState.phoneNumber, error: '' } : formData.phone,
            postalCode: summaryState.postCode ? { value: postalCodeMask[isLatamBrand ? country.abbreviation : 'other'](summaryState.postCode), error: '' } : formData.postalCode,
            state: state && state.name ? { value: state, error: '' } : formData.state,
          };

          setFormData(originalFormData);
          setUserOriginalFormData(originalFormData);
          setUserDataMapped(true);
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.country.value && formData.postalCode.value) {
      const newFormData = { ...formData };
      newFormData.postalCode = { ...newFormData.postalCode, value: postalCodeMask[brand](formData.postalCode.value) };
      setFormData(newFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country, brand]);

  useEffect(() => {
    // search address by postal code
    const { addressesByZipCodeSearch, states } = summaryState.locationData;
    if (addressesByZipCodeSearch) {
      const {
        postCode, address1, address2, state, city,
      } = addressesByZipCodeSearch;

      if (postCode && state && states) {
        fetchCitiesByCountryAndState(formData.country.value.abbreviation, state).then((cities) => {
          const citySearched = city && cities.data ? cities.data.find(singleCity => singleCity.name === city) : formData.city;
          const stateSearched = state ? states.find(singleState => singleState.code === state) : formData.state;

          // NOTE: if the address is not empty, set the address and neighborhood as disabled
          setFormData(actualFormData => ({
            ...actualFormData,
            address: { value: address1, error: '', disabled: Boolean(address1) },
            neighborhood: { value: address2, error: '', disabled: Boolean(address2) },
            city: { value: citySearched, error: '' },
            state: { value: stateSearched, error: '' },
          }));
        });
      }
    }

    if (addressesByZipCodeSearch === null) {
      setFormData(actualFormData => ({
        ...actualFormData,
        address: { ...defaultFormData.address, error: myDataLocale.errors.required },
        neighborhood: { ...defaultFormData.neighborhood, error: myDataLocale.errors.required },
        city: { ...defaultFormData.city, error: myDataLocale.errors.required },
        postalCode: { ...defaultFormData.postalCode, error: myDataLocale.errors.postalCode },
        state: { ...defaultFormData.state, error: myDataLocale.errors.required },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryState.locationData.addressesByZipCodeSearch]);

  useEffect(() => {
    if (userDataMapped) {
      // NOTE: disable user data fields if user data is mapped and no errors
      const hasErrors = hasFormValidationErrors({ preventUpdateForm: true });
      setDisableUserDataFields(!hasErrors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDataMapped]);

  useEffect(() => {
    if (byPassConfirmationPayment && successPurchase) {
      handleNavigateAfterSuccess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successPurchase]);

  return (
    <>
      {isLoading && (
        <Styles.LoaderWrapper>
          <LoaderPage progress={loadingProgress} />
        </Styles.LoaderWrapper>
      )}
      <Styles.FastCheckoutWrapper>
        <FastCheckoutHeader
          locale={locale}
          handleBackNavigation={handleBackNavigation}
          isMobile={isMobile}
        />
        {successPurchase && !byPassConfirmationPayment && (
          <FastCheckoutSuccess
            handleFinishSuccessPurchase={handleNavigateAfterSuccess}
            locale={locale.successPage}
          />
        )}
        {!successPurchase && (
          <FastCheckoutCart
            creditCardMethod={creditCardMethod}
            currentCreditCard={currentCreditCard}
            hasDiscount={hasDiscount}
            isCcButtonEnabled={isCcButtonEnabled}
            isCcButtonLoading={isCcButtonLoading}
            isMobile={isMobile}
            locale={locale}
            onFinalize={handleFinalizeCheckoutProcess}
            paymentMethod={paymentMethod}
            product={productDetails}
            productCycle={productCycle}
            renderIframe={renderIframe}
            setCreditCardMethod={setCreditCardMethod}
            isIframeLoaded={isIframeLoaded}
          />
        )}
        {error.error && (
          <Styles.AlertWrapper>
            <AlertV2
              description={error.message}
              key="errors"
              onClose={() => setError({ error: false, message: '', title: '' })}
              title={error.title}
              variant="critical"
              width="full"
              withoutBackground={false}
            />
          </Styles.AlertWrapper>
        )}
        <ReCaptcha
          action="fastcheckout"
          elementID="g-recaptcha-fastcheckout"
          sitekey={config.RECAPTCHA_SITE_KEY}
          verifyCallback={handleRecaptchaToken}
        />
      </Styles.FastCheckoutWrapper>
    </>
  );
};

export default withErrorBoundary(FastCheckout, 'FastCheckout');
